// What the campaign header says about delivery (canvas boards 12 and 14,
// 2026-09-18): a permanent chip beside the name (LIVE, PAUSED, NEEDS
// ATTENTION…), "checked 4:28 PM" at the end of the meta line, and a third
// line only when there is more to say — a pause reason, a repair in
// progress, an attention sentence with the server's reason.
//
// Until now the header carried `CampaignDeliveryNotice`: "status unverified"
// whenever the last provider observation was older than thirty minutes, a
// binding-repair bookkeeping line whose default reason was a server string,
// and "Last known active · Sep 18, 2026, 4:28 PM". None of it was a thing an
// operator could act on, and "unverified" read as an alarm for what is the
// ordinary gap between two fifteen-minute observations.
//
// This projection says what the campaign is doing, when that was last
// checked, and — only when there is something to do — what. It deliberately
// does NOT reuse `campaignStatusKind`'s thirty-minute downgrade: that rule
// feeds the dashboard dot, pins, productions and venues, whose tests pin it.
// Here a check older than two hours becomes a note ("last checked …"), never
// a headline, and a completed repair prints nothing at all.
//
// 2026-10-19: a campaign whose flights are all over says so. "Ended" once the
// provider was seen stopped, "Ended · stop not confirmed" until then (on The
// Trade Desk the ad groups stay on and the flight window stops spend), and
// "Flight ended · still delivering" when Meta still delivers after the end.
// Between flights the chip names the next flight. The server's state is the
// verdict: an error it still records (the publish log keeps it) no longer
// turns a scheduled or ended campaign into Needs attention here.
//
// 2026-10-21 (campaign lifecycle 2a): Archive no longer reads Ended at once.
// An archived campaign says "Stopping delivery on …" until the provider is
// seen stopped after the archive, and "Stop not confirmed · still delivering
// on …" when it is seen delivering instead. SAM now switches a finished The
// Trade Desk campaign's ad groups off within about fifteen minutes, so "ad
// groups still on" is the short wait before that, never the resting state.
//
// 2026-10-21 (campaign lifecycle 2b): an Extend-flight request says itself. While SAM
// works on it the line reads "Extending…" (the chip too while it holds The Trade Desk's ad
// groups off, since the delivery word is then its doing); a failed or long-held one reads
// "Extension needs attention", and a provider create SAM cannot confirm reads "Extension
// could not be confirmed: duplicate the campaign to run it" (Gio: no release button).

import type { CampaignOperationalStatus } from '@/api/types';
import { fmtDayShort, fmtTime, isInstantFresh } from '@/lib/datetime';
import { EXTENSION_UNCONFIRMED_SENTENCE, type FlightExtensionSignal } from '@/lib/flight-extension';

export type StatusLineProvider = 'Meta' | 'The Trade Desk';

export type StatusChipTone = 'live' | 'quiet' | 'warn' | 'bad';

export interface CampaignStatusLine {
  /** "Delivering on Meta" — the summary sentence and the attention headline. */
  headline: string;
  /** The permanent chip beside the campaign name: one phrase and a tone. */
  chip: { label: string; tone: StatusChipTone };
  /** The meta line's last item: "checked 4:28 PM", or "last checked 1:12 PM" once it is old. */
  checked?: string;
  /** A pause reason, an audience hold, repair progress. Third line only. */
  note?: string;
  /** For Needs attention: the server's reason, in its words. */
  reason?: string;
  /** True when the line should link to the publish log. */
  attention: boolean;
  /** A blocked binding repair: how many ads the provider could not restore. */
  recoveryBlocked?: { failed: number; total: number };
  /** An Extend-flight request the line speaks for; the page offers to review it. */
  extension?: FlightExtensionSignal['kind'];
}

const CHIP: Record<string, { label: string; tone: StatusChipTone }> = {
  Delivering: { label: 'Live', tone: 'live' },
  Starting: { label: 'Starting', tone: 'quiet' },
  Scheduled: { label: 'Scheduled', tone: 'quiet' },
  Paused: { label: 'Paused', tone: 'warn' },
  'Paused by SAM': { label: 'Paused by SAM', tone: 'bad' },
  Pausing: { label: 'Pausing', tone: 'warn' },
  'Partially delivering': { label: 'Partially active', tone: 'warn' },
  'Needs attention': { label: 'Needs attention', tone: 'bad' },
  'Not yet checked': { label: 'Not yet checked', tone: 'quiet' },
  Ended: { label: 'Ended', tone: 'quiet' },
};

/** A check older than this reads "last checked", never a verdict. */
export const STATUS_LINE_FRESH_MS = 2 * 60 * 60_000;

const SILENT_STATES = new Set(['draft', 'armed', 'ready_to_publish']);

/** The word for what the provider is doing, from the server's state or the last observation. */
function word(status: CampaignOperationalStatus): { word: string; attention: boolean } {
  const plain = (w: string) => ({ word: w, attention: false });
  switch (status.state) {
    case 'live': return plain('Delivering');
    case 'starting': return plain('Starting');
    case 'scheduled': return plain('Scheduled');
    case 'paused': return plain('Paused');
    case 'paused_by_system': return plain('Paused by SAM');
    case 'pausing': return plain('Pausing');
    case 'partially_active': return plain('Partially delivering');
    case 'needs_attention': return { word: 'Needs attention', attention: true };
    default: break;
  }
  // `unverified`: the server has no current evidence, so say what it last saw.
  switch (status.observed_state) {
    case 'active': return plain(status.requested_state === 'paused' ? 'Pausing' : 'Delivering');
    case 'paused': {
      // Who paused it, when the server says (2026-10-19): the flight reconcile's
      // pause is the schedule's, never "Paused by SAM".
      if (status.pause_origin === 'schedule') return plain('Scheduled');
      if (status.pause_origin === 'user') return plain('Paused');
      if (status.pause_origin === 'system') return plain('Paused by SAM');
      return plain(status.pause_reason && status.requested_state === 'live' ? 'Paused by SAM' : 'Paused');
    }
    case 'starting': return plain('Starting');
    case 'partially_active': return plain('Partially delivering');
    case 'blocked': return { word: 'Needs attention', attention: true };
    default: return plain('Not yet checked');
  }
}

export function campaignStatusLine(
  status: CampaignOperationalStatus | null | undefined,
  provider: StatusLineProvider,
  nowMs: number,
  extension?: FlightExtensionSignal | null,
): CampaignStatusLine | null {
  const line = deliveryLine(status, provider, nowMs);
  if (!line || !extension) return line;
  return withExtension(line, extension, provider);
}

/**
 * The extension's words on top of the delivery line. Never `attention`: that links the
 * publish log, which records provider syncs and not extensions; the page links the
 * extension itself instead.
 */
function withExtension(
  line: CampaignStatusLine,
  { kind, row }: FlightExtensionSignal,
  provider: StatusLineProvider,
): CampaignStatusLine {
  const dates = `${fmtDayShort(row.start_date)} → ${fmtDayShort(row.end_date)}`;
  const notes: string[] = [];
  let chip = line.chip;
  if (kind === 'unconfirmed') {
    chip = { label: 'Extension not confirmed', tone: 'bad' };
    notes.push(EXTENSION_UNCONFIRMED_SENTENCE);
  } else if (kind === 'attention') {
    chip = { label: 'Extension needs attention', tone: 'bad' };
    notes.push(`Extension needs attention · new flight ${dates}`);
  } else {
    const cancelling = row.cancel_requested_at ? 'Cancelling the extension…' : 'Extending…';
    notes.push(`${cancelling} new flight ${dates}`);
    if (row.activation_hold) {
      chip = { label: 'Extending…', tone: 'quiet' };
      notes.push(`${provider} ad groups are paused until the new flight is added`);
    }
  }
  if (line.note) notes.push(line.note);
  return { ...line, chip, note: notes.join(' · '), extension: kind };
}

function deliveryLine(
  status: CampaignOperationalStatus | null | undefined,
  provider: StatusLineProvider,
  nowMs: number,
): CampaignStatusLine | null {
  if (!status || SILENT_STATES.has(status.state)) return null;
  const recovery = status.recovery;
  // A blocked repair outranks the calendar: the provider holds ads the campaign no
  // longer owns, and its Retry stays open after the flights end (2026-10-19).
  if (recovery && recovery.status === 'blocked') return blockedRecoveryLine(status, provider, nowMs, recovery);
  // Archived (`requested_state` ended) and not yet seen stopped: the server
  // reads `pausing` until a check after the archive finds the provider
  // stopped, `needs_attention` when it finds it delivering (2026-10-21).
  if (status.requested_state === 'ended' && (status.state === 'pausing' || status.state === 'needs_attention')) {
    return archivedLine(status, provider, nowMs);
  }
  if (status.state === 'ended') {
    // Stopped, or archived. The day it ended only when its flights are over:
    // an archived campaign may have stopped before its last flight.
    const day = status.phase === 'finished' && status.last_flight_end ? ` ${fmtDayShort(status.last_flight_end)}` : '';
    return {
      headline: `Ended${day} on ${provider}`,
      chip: CHIP.Ended!,
      checked: checkedItem(status, nowMs),
      attention: false,
    };
  }
  if (status.phase === 'finished' && (status.state === 'unverified' || status.state === 'needs_attention')) {
    return finishedLine(status, provider, nowMs);
  }
  const notes: string[] = [];

  // The server's state is the verdict. Until 2026-10-19 any recorded reason
  // turned the line into Needs attention whatever the state said, so an old
  // error kept a campaign the schedule had paused reading as broken.
  const { word: base, attention } = word(status);
  const reason = attention ? (status.reason ?? status.pause_reason ?? undefined) : undefined;

  let headline = `${base} on ${provider}`;
  let chip = CHIP[base] ?? { label: base, tone: attention ? 'bad' : 'quiet' };
  if (base === 'Scheduled' && status.next_flight_date) {
    const next = fmtDayShort(status.next_flight_date);
    if (status.phase === 'between_flights') {
      // Between flights is said in the header, not only in Section 7.
      headline = `Between flights on ${provider} · next flight ${next}`;
      chip = { label: `Between flights · next ${next}`, tone: 'quiet' };
    } else {
      headline += ` · starts ${next}`;
      if (status.phase === 'before_first_flight') chip = { label: `Scheduled · starts ${next}`, tone: 'quiet' };
    }
  }

  if (!attention && status.pause_reason && (base === 'Paused by SAM' || base === 'Pausing' || base === 'Paused')) {
    notes.push(status.pause_reason);
  }
  // Some audiences match nobody and SAM holds what targets them; the rest deliver (0024).
  if (status.audience_hold) {
    notes.push(status.audience_hold);
  }
  if (recovery && (recovery.status === 'repairing' || recovery.status === 'waiting')) {
    notes.push(
      `Repairing creative bindings · ${recovery.completed_ads} of ${recovery.total_ads} ads` +
        (recovery.next_retry_at ? ` · next retry ${fmtTime(recovery.next_retry_at)}` : ''),
    );
  }
  // An old check is said once, in the meta line ("last checked …"). The note
  // that promised the next check within fifteen minutes is gone (2026-10-19):
  // it only ever showed after two hours without one.

  return {
    headline,
    chip,
    checked: checkedItem(status, nowMs),
    note: notes.length > 0 ? notes.join(' · ') : undefined,
    reason,
    attention,
  };
}

/**
 * An archived campaign whose stop the provider has not yet confirmed. Stopping
 * is the ordinary wait for the next check after the archive, so it is quiet;
 * still delivering after an archive is something to look at.
 */
function archivedLine(status: CampaignOperationalStatus, provider: StatusLineProvider, nowMs: number): CampaignStatusLine {
  const checked = checkedItem(status, nowMs);
  if (status.state === 'needs_attention') {
    return {
      headline: `Stop not confirmed · still delivering on ${provider}`,
      chip: { label: 'Stop not confirmed', tone: 'bad' },
      checked,
      reason: status.reason ?? undefined,
      attention: true,
    };
  }
  return {
    headline: `Stopping delivery on ${provider}`,
    chip: { label: 'Stopping delivery', tone: 'quiet' },
    checked,
    attention: false,
  };
}

/**
 * A published campaign whose flights are all over and that the provider has
 * not been seen to stop (`unverified`), or that Meta still delivers after the
 * end (`needs_attention`). The server decides which; this only words it.
 */
function finishedLine(status: CampaignOperationalStatus, provider: StatusLineProvider, nowMs: number): CampaignStatusLine {
  const day = status.last_flight_end ? ` ${fmtDayShort(status.last_flight_end)}` : '';
  const checked = checkedItem(status, nowMs);
  if (status.state === 'needs_attention') {
    return {
      headline: `Flight ended${day} · still delivering on ${provider}`,
      chip: { label: 'Flight ended · still delivering', tone: 'bad' },
      checked,
      reason: status.reason ?? undefined,
      attention: true,
    };
  }
  // The Trade Desk's ad groups stay on after the last flight until SAM's next
  // fifteen-minute pass switches them off (2026-10-21; until then they stayed
  // on for good). The flight window stops spend meanwhile, so this is said
  // plainly, not as an alarm.
  if (provider === 'The Trade Desk' && status.observed_state === 'active' && !status.stale) {
    return {
      headline: `Flight ended${day} on ${provider} · ad groups still on`,
      chip: { label: 'Ended · stop not confirmed', tone: 'quiet' },
      checked,
      note: `${provider} ad groups are still on until SAM switches them off; its flight window stops spend`,
      attention: false,
    };
  }
  return {
    headline: `Flight ended${day} on ${provider} · stop not confirmed`,
    chip: { label: 'Ended · stop not confirmed', tone: 'warn' },
    checked,
    attention: false,
  };
}

/**
 * A blocked repair is the one repair state that is a headline: the provider
 * holds ads the campaign no longer owns and nothing will fix it without a
 * person.
 */
function blockedRecoveryLine(
  status: CampaignOperationalStatus,
  provider: StatusLineProvider,
  nowMs: number,
  recovery: NonNullable<CampaignOperationalStatus['recovery']>,
): CampaignStatusLine {
  const failed = Math.max(0, recovery.total_ads - recovery.completed_ads);
  const reason = status.reason ?? recovery.reason ?? undefined;
  return {
    headline: `Needs attention · ${provider} could not restore ${failed} of ${recovery.total_ads} ad${recovery.total_ads === 1 ? '' : 's'}`,
    chip: CHIP['Needs attention']!,
    checked: checkedItem(status, nowMs),
    reason,
    attention: true,
    recoveryBlocked: { failed, total: recovery.total_ads },
  };
}

/** The meta line's item: when the provider was last asked. */
function checkedItem(status: CampaignOperationalStatus, nowMs: number): string | undefined {
  if (!status.observed_at) return undefined;
  return isInstantFresh(status.observed_at, STATUS_LINE_FRESH_MS, nowMs)
    ? `checked ${fmtTime(status.observed_at)}`
    : `last checked ${fmtTime(status.observed_at)}`;
}
